import * as signalR from "@microsoft/signalr";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AUTH_TOKEN_STORAGE } from "../constants/localStorageKeys";
import { safeStorage } from "../utils/storage";

interface IChatMessage {
  id: string;
  patientId: number;
  message: string;
  sender: "user" | "ai";
  timestamp: Date;
  isStreaming?: boolean;
}

interface IChatMessageChunk {
  patientId: number;
  chunk: string;
  isComplete: boolean;
  timestamp: string;
}

interface IChatError {
  patientId: number;
  error: string;
  timestamp: string;
}

interface IUseAiChatProps {
  patientId: number;
  apiBaseUrl?: string;
}

interface ISendMessageParams {
  dataType: "measurements" | "surveys" | "gvhd_risk";
  measurementTypes?: string[];
  surveyCodes?: string[];
  dataTypeDisplayName: string;
}

export const useAiChat = ({
  patientId,
  apiBaseUrl = process.env.REACT_APP_API_URL,
}: IUseAiChatProps) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentStreamingId = useRef<string>("");

  useEffect(() => {
    if (!apiBaseUrl) return undefined;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/ai-chat-hub`, {
        accessTokenFactory: () => {
          try {
            const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);
            if (tokenResponse) {
              const { idToken } = JSON.parse(tokenResponse);
              if (idToken) {
                return idToken;
              }
            }
          } catch (error) {
            console.error(
              "useAiChat: Failed to parse token from localStorage:",
              error
            );
          }
          return "";
        },
      })
      .withAutomaticReconnect()
      .build();

    newConnection
      .start()
      .then(() => {
        console.log("SignalR Connected for AI Chat");
        setIsConnected(true);
        newConnection.invoke("JoinPatientChat", patientId.toString());
      })
      .catch((err) => {
        console.error("SignalR Connection Error: ", err);
        toast.error("Failed to connect to AI chat service");
      });

    newConnection.on("ReceiveMessageChunk", (data: IChatMessageChunk) => {
      if (data.patientId === patientId) {
        if (!data.isComplete) {
          if (currentStreamingId.current === "") {
            currentStreamingId.current = `ai-${Date.now()}`;
            setMessages((prev) => {
              const messagesWithoutLoading = prev.filter(
                (msg) => !msg.id.startsWith("loading-")
              );
              return [
                ...messagesWithoutLoading,
                {
                  id: currentStreamingId.current,
                  patientId,
                  message: data.chunk,
                  sender: "ai",
                  timestamp: new Date(),
                  isStreaming: true,
                },
              ];
            });
          } else {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === currentStreamingId.current
                  ? { ...msg, message: msg.message + data.chunk }
                  : msg
              )
            );
          }
        } else if (currentStreamingId.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === currentStreamingId.current
                ? {
                    ...msg,
                    message: msg.message + data.chunk,
                    isStreaming: false,
                  }
                : msg
            )
          );
          currentStreamingId.current = "";
        } else if (data.chunk) {
          setMessages((prev) => [
            ...prev,
            {
              id: `ai-${Date.now()}`,
              patientId,
              message: data.chunk,
              sender: "ai",
              timestamp: new Date(),
              isStreaming: false,
            },
          ]);
        }
      }
    });

    newConnection.on("ReceiveError", (data: IChatError) => {
      if (data.patientId === patientId) {
        console.error("AI Chat Error:", data.error);
        toast.error(data.error);
        setMessages((prev) =>
          prev.filter((msg) => !msg.id.startsWith("loading-"))
        );
        currentStreamingId.current = "";
      }
    });

    newConnection.onclose(() => {
      setIsConnected(false);
      console.log("SignalR Disconnected");
    });

    newConnection.onreconnected(() => {
      setIsConnected(true);
      console.log("SignalR Reconnected");
      newConnection.invoke("JoinPatientChat", patientId.toString());
    });

    setConnection(newConnection);

    return () => {
      newConnection.off("ReceiveMessageChunk");
      newConnection.off("ReceiveError");
    };
  }, [patientId, apiBaseUrl]);

  const sendMessage = useCallback(
    async (params: ISendMessageParams) => {
      if (!connection || !isConnected) {
        toast.error("Not connected to AI chat service");
        return;
      }

      // Validate required fields based on data type
      if (
        params.dataType === "measurements" &&
        (!params.measurementTypes || params.measurementTypes.length === 0)
      ) {
        return;
      }

      if (
        params.dataType === "surveys" &&
        (!params.surveyCodes || params.surveyCodes.length === 0)
      ) {
        return;
      }

      // GVHD risk assessment doesn't require additional parameters

      const userMessageId = `user-${Date.now()}`;

      try {
        setIsLoading(true);

        const userMessage: IChatMessage = {
          id: userMessageId,
          patientId,
          message: `Requested insights for ${params.dataTypeDisplayName}`,
          sender: "user",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);

        const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);
        if (!tokenResponse) {
          throw new Error("No token found");
        }
        const { idToken } = JSON.parse(tokenResponse);
        if (!idToken) {
          throw new Error("ID token is missing");
        }

        const response = await fetch(`${apiBaseUrl}/Portal/AiChat/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            patientId,
            ...params,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to send message");
        }

        let logMessage = "GVHD Risk Assessment";
        if (params.dataType === "measurements") {
          logMessage = params.measurementTypes?.join(", ") || "";
        } else if (params.dataType === "surveys") {
          logMessage = params.surveyCodes?.join(", ") || "";
        }
        console.log("Message sent:", logMessage);
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message to AI");
        setMessages((prev) => prev.filter((msg) => msg.id !== userMessageId));
      } finally {
        setIsLoading(false);
      }
    },
    [connection, isConnected, patientId, apiBaseUrl]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    currentStreamingId.current = "";
  }, []);

  useEffect(() => {
    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [connection]);

  return {
    messages,
    isConnected,
    isLoading,
    sendMessage,
    clearMessages,
    setMessages,
  };
};
